/**
 * Viewport controls: collapse panels, hide the HUD, and go fullscreen.
 *
 * On a desktop the panels sit beside the map. On a phone they sat *on* it —
 * two stacked trays covering 54% of a 375x812 screen, with the only way out
 * being a HIDE button that took the whole interface away including itself.
 * "All of it or none of it" is not a real choice when the panels are how you
 * reach the layers.
 *
 * So on small screens the panels collapse to their title bars and open one at
 * a time, like an accordion. The map is always visible; a tap gets a panel
 * back. Desktop is untouched — there is room there, and collapsing would only
 * add a click.
 */

import { cycleTheme } from "../theme/palette.js";
import { isShortcut } from "./keys.js";

const HIDDEN_CLASS = "ui-hidden";

const COLLAPSED_CLASS = "is-stowed";

/** Matches the breakpoint the mobile layout uses in app.css. */
const NARROW = "(max-width: 820px)";

/**
 * The compass: shows which way the map faces, and restores north when tapped.
 *
 * MapLibre's NavigationControl already carries one, but it sits in the corner
 * in its own visual language. This is the same job in the HUD's, next to the
 * other controls, where someone looking for it will actually find it.
 *
 * Bearing is read on every `rotate` event rather than polled — the needle has
 * to agree with the map at all times or it is worse than no needle.
 */
export function mountCompass(map) {
  const button = document.getElementById("compass");
  const needle = document.getElementById("compass-needle");
  if (!button || !map) return { destroy() {} };

  const flat = document.getElementById("top-down");

  function sync() {
    const bearing = map.getBearing();
    // Counter-rotate: the needle points at north while the map turns.
    needle?.setAttribute("transform", `rotate(${-bearing} 12 12)`);
    const aligned = Math.abs(bearing) < 0.5;
    if (flat) {
      const isFlat = map.getPitch() < 0.5;
      flat.dataset.flat = String(isFlat);
      flat.title = isFlat ? "Already looking straight down" : "Look straight down (T)";
    }
    button.dataset.aligned = String(aligned);
    button.title = aligned
      ? "Facing north"
      : `Facing ${Math.round((bearing + 360) % 360)}° — click to face north`;
  }

  // Straight down. Bearing is left alone: someone who turned the map to line
  // a street up did that on purpose, and flattening should not also spin it.
  function lookDown() {
    map.easeTo({ pitch: 0, duration: 600, essential: true });
  }
  flat?.addEventListener("click", lookDown);

  function faceNorth() {
    // Pitch is left alone: someone who tilted the city into a skyline asked
    // for that, and flattening it uninvited would undo their work.
    map.easeTo({ bearing: 0, duration: 600, essential: true });
  }

  button.addEventListener("click", faceNorth);
  map.on("rotate", sync);
  map.on("pitch", sync);
  map.on("move", sync);
  sync();

  return {
    sync, faceNorth, lookDown,
    destroy() { map.off("rotate", sync); map.off("pitch", sync); map.off("move", sync); },
  };
}

export function mountViewport({ onChange } = {}) {
  const root = document.body;
  const narrow = window.matchMedia(NARROW);

  // ── Stow panels behind a caret ──────────────────────────────────────────
  // Independent of the mobile accordion below: this hides a panel entirely,
  // at any size, leaving a caret against the edge to bring it back. The panel
  // slides out rather than being removed, so nothing reflows on return.
  const TABS = [
    ["layers-tab", "layers-panel"],
    ["incidents-tab", "incidents-panel"],
    ["tools-tab", "tools-panel"],
  ];

  for (const [tabId, panelId] of TABS) {
    const tab = document.getElementById(tabId);
    const panel = document.getElementById(panelId);
    if (!tab || !panel) continue;

    tab.addEventListener("click", () => {
      const stowed = panel.classList.toggle(COLLAPSED_CLASS);
      tab.setAttribute("aria-expanded", String(!stowed));
      tab.title = stowed ? `Show ${panelId.split("-")[0]}` : "Hide";
      if (onChange) onChange();
    });
  }

  // ── One collapse control per panel ──────────────────────────────────────
  // There used to be two: an accordion on the title bar and the caret tab.
  // Two carets on one panel, only one of which did anything you could
  // predict. The tab is the survivor — it stays put when the panel is gone,
  // which the title bar cannot.
  //
  // Phones still start with the panels out of the way, but by stowing them
  // rather than collapsing them, so the same control governs both states.
  function applyLayout() {
    for (const [tabId, panelId] of TABS) {
      const tab = document.getElementById(tabId);
      const panel = document.getElementById(panelId);
      if (!tab || !panel) continue;
      const stow = narrow.matches;
      panel.classList.toggle(COLLAPSED_CLASS, stow);
      tab.setAttribute("aria-expanded", String(!stow));
    }
    if (onChange) onChange();
  }

  /**
   * On a phone, tapping away from an open panel puts it away.
   *
   * A panel covers most of a 375px screen, so the map underneath is both the
   * thing you opened the panel to look at and the only place there is to tap.
   * Requiring the caret — a 24px target at the panel's edge, which the panel
   * itself has just pushed to somewhere you were not looking — is the sort of
   * thing that reads as the interface being stuck.
   *
   * Desktop is left alone: there the panels sit beside the map rather than on
   * it, nothing is covered, and dismissing them on every stray click would be
   * taking something away that costs nothing to leave.
   */
  function stowAllPanels(exceptPanel) {
    let changed = false;
    for (const [tabId, panelId] of TABS) {
      const tab = document.getElementById(tabId);
      const panel = document.getElementById(panelId);
      if (!tab || !panel || panel === exceptPanel) continue;
      if (panel.classList.contains(COLLAPSED_CLASS)) continue;
      panel.classList.add(COLLAPSED_CLASS);
      tab.setAttribute("aria-expanded", "false");
      tab.title = `Show ${panelId.split("-")[0]}`;
      changed = true;
    }
    if (changed && onChange) onChange();
  }

  document.addEventListener("pointerdown", (event) => {
    if (!narrow.matches) return;
    // A tap on the panel is someone using it, and a tap on a caret is that
    // caret's own job — intercepting either here would fight the control.
    if (event.target.closest?.(".panel, .panel-tab")) return;
    // Editors and menus open over the map and own their own dismissal; closing
    // the panel behind one would move the ground under whatever is on top.
    if (event.target.closest?.(".panel-modal, .settings-menu, .scan-card")) return;
    stowAllPanels();
  }, { passive: true });

  applyLayout();
  // Rotating a phone or resizing a window changes which layout applies.
  //
  // Both listeners on purpose. matchMedia's change event is the right signal
  // but does not always arrive when the viewport is driven programmatically
  // rather than by a real window drag, which left panels marked
  // aria-expanded="false" on a desktop where they were plainly open. resize
  // is noisier but never misses; applyLayout is idempotent and cheap enough
  // to run on both.
  narrow.addEventListener("change", applyLayout);
  window.addEventListener("resize", applyLayout);

  /** Hide every panel, leaving only the map and the CRT film. */
  function setChromeHidden(hidden) {
    // The button that used to live in the top bar is gone; the settings menu
    // owns this now and paints its own label from chromeHidden().
    root.classList.toggle(HIDDEN_CLASS, hidden);
    if (onChange) onChange();
  }

  function chromeHidden() {
    return root.classList.contains(HIDDEN_CLASS);
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // Fullscreen is refused without a user gesture, and in some embedded
      // contexts outright. Failing silently is correct: the button simply
      // does nothing rather than throwing into the console.
    }
  }

  document.getElementById("toggle-fullscreen")?.addEventListener("click", toggleFullscreen);

  document.addEventListener("fullscreenchange", () => {
    const button = document.getElementById("toggle-fullscreen");
    if (button) button.setAttribute("aria-pressed", String(!!document.fullscreenElement));
  });

  window.addEventListener("keydown", (event) => {
    if (!isShortcut(event)) return;

    const key = event.key.toLowerCase();
    if (key === "r") {
      event.preventDefault();
      // Route lives in the mobile tools panel now; the shortcut still works
      // at a desk for anyone who knows it.
      document.getElementById("toggle-route-mobile")?.click();
    } else if (key === "y") {
      event.preventDefault();
      cycleTheme();
    } else if (key === "t") {
      event.preventDefault();
      document.getElementById("top-down")?.click();
    } else if (key === "p") {
      // P, not S. S is the back-pedal in WASD now, and walk.js claims it in
      // the capture phase — leaving SCAN on S would not have been a clash so
      // much as a key that silently stopped working. P for "play the tour",
      // which is what SCAN does: it steps through incidents 15s at a time.
      event.preventDefault();
      document.getElementById("scan")?.click();
    } else if (key === "n") {
      event.preventDefault();
      document.getElementById("compass")?.click();
    } else if (key === "f") {
      event.preventDefault();
      toggleFullscreen();
    } else if (key === "h") {
      event.preventDefault();
      setChromeHidden(!chromeHidden());
    } else if (event.key === "Escape" && chromeHidden()) {
      // Escape already exits fullscreen; make it restore the HUD too, so a
      // hidden UI can never become a dead end.
      setChromeHidden(false);
    }
  });

  /** Enter or leave fullscreen deliberately, rather than toggling blind. */
  async function setFullscreen(next) {
    try {
      if (next && !document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else if (!next && document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      // Refused without a user gesture, and outright in some embedded
      // contexts. Failing silently is right: the scan still runs, it just
      // does not fill the screen.
    }
  }

  const isFullscreen = () => Boolean(document.fullscreenElement);

  return {
    setChromeHidden, chromeHidden, toggleFullscreen, setFullscreen, isFullscreen,
    // Exposed so routing can clear the screen before asking for a tap on it.
    stowPanels: stowAllPanels,
  };
}
